import { isValidElement } from 'react'

import { generateSEO } from '@fe-toolkit/web-components/ssr'

import type { ReactNode } from 'react'

export const SITE_URL = 'https://www.giffgaff.com'

const ORGANIZATION_ID = `${SITE_URL}/#organization`
const WEBSITE_ID = `${SITE_URL}/#website`

/** Portal's share image (giffgaff-fe layout-v2.phtml), so both homepage arms unfurl the same. */
const SHARE_IMAGE = `${SITE_URL}/styleguide/images/layout/giffgaff_logo_1200x630.jpg`

export type JsonLdNode = Record<string, unknown>

/**
 * `generateSEO` with two of its defaults corrected. Its default `image` ends in a stray comma,
 * so every route shipped a broken og:image, and it always appends a WebSite JSON-LD block whose
 * publisher logo is `${siteUrl}/logo.png`, which nothing serves. That block is dropped here;
 * pages describe themselves with `jsonLd` instead.
 */
export const seoTags = (options: Parameters<typeof generateSEO>[0]) =>
  generateSEO({ image: SHARE_IMAGE, ...options }).filter((tag) => tag.key !== 'ld+json')

/**
 * giffgaff itself. Defined in full on the homepage, which is the entity home, and referenced
 * by `@id` everywhere else, so the site describes one organisation rather than one per page.
 */
export const organizationNode: JsonLdNode = {
  '@type': 'Organization',
  '@id': ORGANIZATION_ID,
  name: 'giffgaff',
  url: `${SITE_URL}/`,
  // The wordmark on white, 256px square, from Portal's webroot. Google wants a logo that reads
  // on white and is at least 112px; the touch icon this used to name is an app icon (FAAS-940).
  logo: `${SITE_URL}/assets/giffgaff-logo_256.png`,
  image: SHARE_IMAGE,
  // The profiles giffgaff's footer links to.
  sameAs: [
    'https://www.instagram.com/giffgaff/',
    'https://www.facebook.com/giffgaff',
    'https://www.linkedin.com/company/giffgaff/',
    'https://www.youtube.com/giffgaff',
  ],
}

export const websiteNode: JsonLdNode = {
  '@type': 'WebSite',
  '@id': WEBSITE_ID,
  url: `${SITE_URL}/`,
  name: 'giffgaff',
  inLanguage: 'en-GB',
  publisher: { '@id': ORGANIZATION_ID },
}

export const ORGANIZATION_REF = { '@id': ORGANIZATION_ID }

type PageNodeOptions = { url: string; name: string; description: string; type?: string } & JsonLdNode

/** The page itself. `url` must be the canonical, since the `@id`s hang off it. */
export const webPageNode = ({ url, name, description, type = 'WebPage', ...rest }: PageNodeOptions): JsonLdNode => ({
  '@type': type,
  '@id': `${url}#webpage`,
  url,
  name,
  description,
  inLanguage: 'en-GB',
  isPartOf: { '@id': WEBSITE_ID },
  ...rest,
})

/** Home first, then `trail`, which should end with the page itself. */
export const breadcrumbNode = (url: string, trail: { name: string; url: string }[]): JsonLdNode => ({
  '@type': 'BreadcrumbList',
  '@id': `${url}#breadcrumb`,
  itemListElement: [{ name: 'Home', url: `${SITE_URL}/` }, ...trail].map((crumb, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: crumb.name,
    item: crumb.url,
  })),
})

/** For a `<Helmet>`. `<` is escaped so no text in the graph can close the script early. */
export const jsonLd = (graph: JsonLdNode[]) => (
  <script type="application/ld+json">
    {JSON.stringify({ '@context': 'https://schema.org', '@graph': graph }).replace(/</g, '\\u003c')}
  </script>
)

/**
 * The visible text of a React tree, for structured data that must say exactly what the page
 * says. Elements are padded so adjacent paragraphs do not run together, then whitespace
 * collapses and the padding is taken back off any punctuation that follows a link.
 */
export const toPlainText = (node: ReactNode): string => {
  const walk = (child: ReactNode): string => {
    if (typeof child === 'string' || typeof child === 'number') {
      return String(child)
    }

    if (Array.isArray(child)) {
      return child.map(walk).join('')
    }

    if (isValidElement<{ children?: ReactNode }>(child)) {
      return ` ${walk(child.props.children)} `
    }

    return ''
  }

  return walk(node)
    .replace(/\s+/g, ' ')
    .replace(/ ([.,;:!?])/g, '$1')
    .trim()
}
